import * as React from 'react';
import isNil from 'lodash/isNil';
import { connect } from 'react-redux';

import Page from 'components/page/Page';
import PageSection from 'components/page/page-section/PageSection';
import SettingsSidebar from 'modules/settings/SettingsSidebar';
import SidebarPage from 'components/page/sidebar-page/SidebarPage';
import InfluencerComposedMessage from 'components/influencer-composed-message/InfluencerComposedMessage';
import DeleteUser from 'components/delete-user/DeleteUser';

import UserAccount from '../../components/user-account/UserAccount';

import { fetchOrganizationSettings } from 'modules/organizations/reducers/organizationSettings';
import {
  fetchInfluencerProfile,
  resetInfluencerProfile
} from 'modules/influencers/reducers/influencerProfile';

import { __t, applyTranslations } from 'utils/language';

import './user-account-page.scss';

import { Account, Organization } from 'types';

applyTranslations(require('./user-account-page.i18n.json'));

interface StateProps {
  account: Account,
  organization: Organization,
  profileId: number | null,
  hasMultiOrganizations: boolean
}

type DispatchProps = {
  onMount: (organizationId: number, profileId: number) => void,
  onUnmount: () => void
};

type Props = StateProps & DispatchProps;

const UserAccountPage = ({
  account,
  organization,
  profileId,
  hasMultiOrganizations,
  onMount,
  onUnmount
}: Props) => {
  const [orgId] = React.useState(organization?.id ?? null);

  React.useEffect(() => {
    if (orgId === null || profileId === null) {
      return onUnmount;
    }

    onMount(orgId, profileId);

    return onUnmount;
  }, []);

  return (
    <Page documentTitle={__t('user_account_page.title')}>
      {orgId !== null && profileId !== null && (
        <InfluencerComposedMessage
          ignoreIfPaymentDetailsPresent
          sidenoteClassName="user-account-page__sidenote"
        />
      )}
      <SidebarPage
        scrollable
        sidebarWidth="narrow"
        sidebarContent={<SettingsSidebar />}
      >
        <PageSection
          title={__t('user_account_page.account')}
          margin="none"
          titleProps={{ margin: 'top-none' }}
        >
          <UserAccount />
        </PageSection>
        {
          organization?.type === 'influencer'
            && isNil(organization?.agencyOrganizationId)
            && !hasMultiOrganizations && (
            <PageSection>
              <DeleteUser
                account={account}
                orgType={organization?.type}
                profileId={profileId}
              />
            </PageSection>
          )
        }
      </SidebarPage>
    </Page>
  );
};

const mapStateToProps = ({ account, organizations }) => ({
  account: account.currentAccount,
  organization: organizations.currentOrganization,
  hasMultiOrganizations: organizations.list.length > 1,
  profileId: (() => {
    const profile = (organizations?.currentOrganization?.influencerProfiles ?? [])[0] ?? null;

    return profile?.id ?? null;
  })()
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  onMount: (organizationId, profileId) => {
    dispatch(fetchInfluencerProfile(profileId));
    dispatch(fetchOrganizationSettings(organizationId));
  },
  onUnmount: () => {
    dispatch(resetInfluencerProfile());
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(UserAccountPage);
